/**
 * Permission System
 *
 * This file defines all permissions, roles, and draft-related types for the application.
 * Permissions control access to features and actions throughout the dashboard.
 *
 * ⚠️ AUTO-GENERATED FILE - DO NOT EDIT MANUALLY
 * Run 'pnpm codegen:permissions' to regenerate from database
 */

// ============================================================================
// PERMISSION ENUMS
// ============================================================================

export enum Permission {
  /** Erlaubt Zugriff auf den Dashboard Kontakt-/Anfragen-Tab */
  ACCESS_DASHBOARD_CONTACT = 'ACCESS_DASHBOARD_CONTACT',
  /** Erlaubt Zugriff auf den Dashboard Content Management Tab */
  ACCESS_DASHBOARD_CONTENT = 'ACCESS_DASHBOARD_CONTENT',
  /** Zugriff auf die Spielverwaltung im Dashboard */
  ACCESS_DASHBOARD_MATCHES = 'ACCESS_DASHBOARD_MATCHES',
  /** Erlaubt Zugriff auf den Dashboard Sponsor Management Tab */
  ACCESS_DASHBOARD_SPONSORS = 'ACCESS_DASHBOARD_SPONSORS',
  /** Erlaubt Zugriff auf den Dashboard Team Management Tab */
  ACCESS_DASHBOARD_TEAMS = 'ACCESS_DASHBOARD_TEAMS',
  /** Erlaubt Zugriff auf den Dashboard User Management Tab */
  ACCESS_DASHBOARD_USERS = 'ACCESS_DASHBOARD_USERS',
  /** Berechtigungsdefinitionen erstellen und bearbeiten */
  ADMIN_MANAGE_PERMISSIONS = 'ADMIN_MANAGE_PERMISSIONS',
  /** Rollendefinitionen erstellen und bearbeiten */
  ADMIN_MANAGE_ROLES = 'ADMIN_MANAGE_ROLES',
  /** System-Logs und Audit-Trails anzeigen */
  ADMIN_VIEW_LOGS = 'ADMIN_VIEW_LOGS',
  /** Schnuppertermine verwalten (erstellen, bestaetigen, verschieben, absagen) */
  APPOINTMENT_MANAGE = 'APPOINTMENT_MANAGE',
  /** Vorstandsmitglieder-Profile erstellen */
  BOARD_CREATE = 'BOARD_CREATE',
  /** Eigenes Vorstandsmitglieder-Profil erstellen */
  BOARD_CREATE_OWN = 'BOARD_CREATE_OWN',
  /** Vorstandsmitglieder-Profile löschen */
  BOARD_DELETE = 'BOARD_DELETE',
  /** Eigenes Vorstandsmitglieder-Profil löschen */
  BOARD_DELETE_OWN = 'BOARD_DELETE_OWN',
  /** Vorstandsmitglieder-Profile direkt aktualisieren */
  BOARD_UPDATE_ALL = 'BOARD_UPDATE_ALL',
  /** Eigenes Vorstandsmitglieder-Profil aktualisieren */
  BOARD_UPDATE_OWN = 'BOARD_UPDATE_OWN',
  /** Eigenen Vorstands-Avatar hochladen */
  BOARD_UPLOAD_AVATAR = 'BOARD_UPLOAD_AVATAR',
  /** Eigenes Vorstands-Banner hochladen */
  BOARD_UPLOAD_BANNER = 'BOARD_UPLOAD_BANNER',
  /** Alle Vorstandsmitglieder-Profile anzeigen */
  BOARD_VIEW_ALL = 'BOARD_VIEW_ALL',
  /** Kontaktanfragen einem Bearbeiter zuweisen */
  CONTACT_ASSIGN = 'CONTACT_ASSIGN',
  /** Kontaktanfragen loeschen */
  CONTACT_DELETE = 'CONTACT_DELETE',
  /** Auf Kontaktanfragen antworten (Nachrichten senden) */
  CONTACT_RESPOND = 'CONTACT_RESPOND',
  /** Alle Kontaktanfragen anzeigen */
  CONTACT_VIEW_ALL = 'CONTACT_VIEW_ALL',
  /** Beiträge direkt erstellen und veröffentlichen ohne Review */
  CONTENT_CREATE_PUBLISHED = 'CONTENT_CREATE_PUBLISHED',
  /** Beliebige Beiträge löschen */
  CONTENT_DELETE_ALL = 'CONTENT_DELETE_ALL',
  /** Eigene Beiträge löschen */
  CONTENT_DELETE_OWN = 'CONTENT_DELETE_OWN',
  /** Nachrichten als Eilmeldung markieren */
  CONTENT_MARK_BREAKING = 'CONTENT_MARK_BREAKING',
  /** Entwürfe veröffentlichen */
  CONTENT_PUBLISH = 'CONTENT_PUBLISH',
  /** Veröffentlichte Beiträge zurückziehen */
  CONTENT_UNPUBLISH = 'CONTENT_UNPUBLISH',
  /** Beliebige Beiträge direkt aktualisieren (umgeht Review) */
  CONTENT_UPDATE_ALL = 'CONTENT_UPDATE_ALL',
  /** Eigene Beiträge direkt aktualisieren */
  CONTENT_UPDATE_OWN = 'CONTENT_UPDATE_OWN',
  /** Alle Beiträge inkl. Entwürfe anzeigen */
  CONTENT_VIEW_ALL = 'CONTENT_VIEW_ALL',
  /** Eigene Beiträge anzeigen */
  CONTENT_VIEW_OWN = 'CONTENT_VIEW_OWN',
  /** Nur veröffentlichte Beiträge anzeigen (Standard für alle) */
  CONTENT_VIEW_PUBLISHED = 'CONTENT_VIEW_PUBLISHED',
  /** Neue Spiele anlegen */
  MATCHES_CREATE = 'MATCHES_CREATE',
  /** Spiele loeschen */
  MATCHES_DELETE = 'MATCHES_DELETE',
  /** Abgleich mit dem OEFB manuell starten */
  MATCHES_SYNC = 'MATCHES_SYNC',
  /** Alle Spiele bearbeiten (Ergebnis, Spielverlauf, Bericht) */
  MATCHES_UPDATE_ALL = 'MATCHES_UPDATE_ALL',
  /** Spiele der eigenen Mannschaften bearbeiten */
  MATCHES_UPDATE_OWN = 'MATCHES_UPDATE_OWN',
  /** Alle Spiele sehen, auch unveroeffentlichte */
  MATCHES_VIEW_ALL = 'MATCHES_VIEW_ALL',
  /** Sortierung für Vorstandsmitglieder konfigurieren */
  SETTINGS_BOARD_SORT = 'SETTINGS_BOARD_SORT',
  /** Sortierung für Teams konfigurieren */
  SETTINGS_TEAM_SORT = 'SETTINGS_TEAM_SORT',
  /** Sortierung für Trainer konfigurieren */
  SETTINGS_TRAINER_SORT = 'SETTINGS_TRAINER_SORT',
  /** Neue Sponsoren erstellen */
  SPONSORS_CREATE = 'SPONSORS_CREATE',
  /** Sponsoren löschen */
  SPONSORS_DELETE = 'SPONSORS_DELETE',
  /** Automatische Sponsor-Tier-Berechnung überschreiben */
  SPONSORS_OVERRIDE_TIER = 'SPONSORS_OVERRIDE_TIER',
  /** Sponsoren direkt aktualisieren (umgeht Review) */
  SPONSORS_UPDATE_ALL = 'SPONSORS_UPDATE_ALL',
  /** Alle Sponsoren anzeigen */
  SPONSORS_VIEW_ALL = 'SPONSORS_VIEW_ALL',
  /** Trainer zu Teams zuweisen oder entfernen */
  TEAMS_ASSIGN_TRAINERS = 'TEAMS_ASSIGN_TRAINERS',
  /** Neue Teams erstellen */
  TEAMS_CREATE = 'TEAMS_CREATE',
  /** Teams löschen */
  TEAMS_DELETE = 'TEAMS_DELETE',
  /** Beliebige Teams direkt aktualisieren (umgeht Review) */
  TEAMS_UPDATE_ALL = 'TEAMS_UPDATE_ALL',
  /** Team-Bild aktualisieren (gilt für Entwürfe und Veröffentlichte) */
  TEAMS_UPDATE_IMAGE = 'TEAMS_UPDATE_IMAGE',
  /** Team-Name aktualisieren (gilt für Entwürfe und Veröffentlichte) */
  TEAMS_UPDATE_NAME = 'TEAMS_UPDATE_NAME',
  /** Eigene Teams direkt aktualisieren */
  TEAMS_UPDATE_OWN = 'TEAMS_UPDATE_OWN',
  /** Zugewiesene Trainer aktualisieren (gilt für Entwürfe und Veröffentlichte) */
  TEAMS_UPDATE_TRAINERS = 'TEAMS_UPDATE_TRAINERS',
  /** Alle Teams anzeigen */
  TEAMS_VIEW_ALL = 'TEAMS_VIEW_ALL',
  /** Teams anzeigen, denen der Benutzer als Trainer zugewiesen ist */
  TEAMS_VIEW_OWN = 'TEAMS_VIEW_OWN',
  /** Trainer-Profile erstellen */
  TRAINERS_CREATE = 'TRAINERS_CREATE',
  /** Eigenes Trainer-Profil erstellen */
  TRAINERS_CREATE_OWN = 'TRAINERS_CREATE_OWN',
  /** Trainer-Profile löschen */
  TRAINERS_DELETE = 'TRAINERS_DELETE',
  /** Eigenes Trainer-Profil löschen */
  TRAINERS_DELETE_OWN = 'TRAINERS_DELETE_OWN',
  /** Beliebige Trainer-Profile direkt aktualisieren */
  TRAINERS_UPDATE_ALL = 'TRAINERS_UPDATE_ALL',
  /** Eigenes Trainer-Profil direkt aktualisieren */
  TRAINERS_UPDATE_OWN = 'TRAINERS_UPDATE_OWN',
  /** Eigenen Trainer-Avatar hochladen */
  TRAINERS_UPLOAD_AVATAR = 'TRAINERS_UPLOAD_AVATAR',
  /** Eigenes Trainer-Banner hochladen */
  TRAINERS_UPLOAD_BANNER = 'TRAINERS_UPLOAD_BANNER',
  /** Alle Trainer-Profile anzeigen */
  TRAINERS_VIEW_ALL = 'TRAINERS_VIEW_ALL',
  /** Neue Benutzer erstellen */
  USERS_CREATE_USER = 'USERS_CREATE_USER',
  /** Benutzerkonten löschen */
  USERS_DELETE = 'USERS_DELETE',
  /** Berechtigungen und Rollen zu Benutzern zuweisen */
  USERS_MANAGE_PERMISSIONS = 'USERS_MANAGE_PERMISSIONS',
  /** Passwort-Resets für beliebige Benutzer auslösen */
  USERS_RESET_PASSWORD = 'USERS_RESET_PASSWORD',
  /** Beliebige Benutzerprofile direkt aktualisieren (umgeht Review) */
  USERS_UPDATE_ALL = 'USERS_UPDATE_ALL',
  /** Eigenes Profil direkt aktualisieren ohne Review */
  USERS_UPDATE_OWN = 'USERS_UPDATE_OWN',
  /** Eigenen Benutzer-Avatar hochladen */
  USERS_UPLOAD_AVATAR = 'USERS_UPLOAD_AVATAR',
  /** Eigenes Benutzer-Banner hochladen */
  USERS_UPLOAD_BANNER = 'USERS_UPLOAD_BANNER',
  /** Alle Benutzerprofile anzeigen */
  USERS_VIEW_ALL = 'USERS_VIEW_ALL',
  /** Eigenes Profil anzeigen (Standard für alle authentifizierten Benutzer) */
  USERS_VIEW_OWN = 'USERS_VIEW_OWN',
}

// ============================================================================
// ROLE ENUMS
// ============================================================================

export enum Role {
  /** Super Administrator with full access to all features */
  ADMIN = 'ADMIN',
  /** Anfragen-Manager: verwaltet Kontaktanfragen, Schnupper- und Probetermine und sieht alle Mannschaften */
  ANFRAGEN_MANAGER = 'ANFRAGEN_MANAGER',
  /** Can create and edit content but requires review for publishing */
  CONTENT_EDITOR = 'CONTENT_EDITOR',
  /** Can create and manage news items on the website. */
  CONTENT_MANAGER = 'CONTENT_MANAGER',
  /** Verwaltet alle Spiele: Ergebnisse, Spielverlauf, Berichte und OEFB-Abgleich */
  SPIELE_MANAGER = 'SPIELE_MANAGER',
  /** Full control over sponsor management */
  SPONSOR_MANAGER = 'SPONSOR_MANAGER',
  /** Full control over team management */
  TEAM_MANAGER = 'TEAM_MANAGER',
  /** Can manage own team and profile */
  TRAINER = 'TRAINER',
  /** Basic authenticated user with access to own profile only (excludes Trainer/Board profiles) */
  USER = 'USER',
  /** Full control over user management */
  USER_MANAGER = 'USER_MANAGER',
}

// ============================================================================
// DRAFT ENUMS
// ============================================================================

export enum DraftType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export enum DraftStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum EntityType {
  USER = 'user',
  CONTENT = 'content',
  TEAM = 'team',
  SPONSOR = 'sponsor',
  TRAINER = 'trainer',
  BOARD = 'board',
  MATCH = 'match',
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface PermissionDefinition {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface RoleDefinition {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  permissions?: PermissionDefinition[];
}

export interface UserPermissions {
  user_id: string;
  permissions: string[];
  roles?: RoleDefinition[];
}

export interface Draft<T = any> {
  id: number;
  entity_id?: number | string;
  draft_data: T;
  draft_type: DraftType;
  created_by: string;
  created_at: string;
  status: DraftStatus;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  changed_fields?: string[];
}

export interface ProfileDraft extends Draft {
  profile_id?: string;
}

export interface NewsDraft extends Draft {
  news_id?: number;
}

export interface TeamDraft extends Draft {
  team_id?: number;
}

export interface SponsorDraft extends Draft {
  sponsor_id?: number;
}

export interface TrainerDraft extends Draft {
  trainer_id?: string;
}

export interface BoardMemberDraft extends Draft {
  boardmember_id?: string;
}

// ============================================================================
// PERMISSION CATEGORIES (for convenience grouping)
// ============================================================================

export const PermissionCategories = {
  DASHBOARD_ACCESS: [
    Permission.ACCESS_DASHBOARD_CONTACT,
    Permission.ACCESS_DASHBOARD_CONTENT,
    Permission.ACCESS_DASHBOARD_MATCHES,
    Permission.ACCESS_DASHBOARD_SPONSORS,
    Permission.ACCESS_DASHBOARD_TEAMS,
    Permission.ACCESS_DASHBOARD_USERS,
  ],

  USER_MANAGEMENT: [
    Permission.USERS_CREATE_USER,
    Permission.USERS_DELETE,
    Permission.USERS_MANAGE_PERMISSIONS,
    Permission.USERS_RESET_PASSWORD,
    Permission.USERS_UPDATE_ALL,
    Permission.USERS_UPDATE_OWN,
    Permission.USERS_UPLOAD_AVATAR,
    Permission.USERS_UPLOAD_BANNER,
    Permission.USERS_VIEW_ALL,
    Permission.USERS_VIEW_OWN,
  ],

  CONTENT_MANAGEMENT: [
    Permission.CONTENT_CREATE_PUBLISHED,
    Permission.CONTENT_DELETE_ALL,
    Permission.CONTENT_DELETE_OWN,
    Permission.CONTENT_MARK_BREAKING,
    Permission.CONTENT_PUBLISH,
    Permission.CONTENT_UNPUBLISH,
    Permission.CONTENT_UPDATE_ALL,
    Permission.CONTENT_UPDATE_OWN,
    Permission.CONTENT_VIEW_ALL,
    Permission.CONTENT_VIEW_OWN,
    Permission.CONTENT_VIEW_PUBLISHED,
  ],

  TEAM_MANAGEMENT: [
    Permission.TEAMS_ASSIGN_TRAINERS,
    Permission.TEAMS_CREATE,
    Permission.TEAMS_DELETE,
    Permission.TEAMS_UPDATE_ALL,
    Permission.TEAMS_UPDATE_IMAGE,
    Permission.TEAMS_UPDATE_NAME,
    Permission.TEAMS_UPDATE_OWN,
    Permission.TEAMS_UPDATE_TRAINERS,
    Permission.TEAMS_VIEW_ALL,
    Permission.TEAMS_VIEW_OWN,
  ],

  SPONSOR_MANAGEMENT: [
    Permission.SPONSORS_CREATE,
    Permission.SPONSORS_DELETE,
    Permission.SPONSORS_OVERRIDE_TIER,
    Permission.SPONSORS_UPDATE_ALL,
    Permission.SPONSORS_VIEW_ALL,
  ],

  TRAINER_MANAGEMENT: [
    Permission.TRAINERS_CREATE,
    Permission.TRAINERS_CREATE_OWN,
    Permission.TRAINERS_DELETE,
    Permission.TRAINERS_DELETE_OWN,
    Permission.TRAINERS_UPDATE_ALL,
    Permission.TRAINERS_UPDATE_OWN,
    Permission.TRAINERS_UPLOAD_AVATAR,
    Permission.TRAINERS_UPLOAD_BANNER,
    Permission.TRAINERS_VIEW_ALL,
  ],

  BOARD_MANAGEMENT: [
    Permission.BOARD_CREATE,
    Permission.BOARD_CREATE_OWN,
    Permission.BOARD_DELETE,
    Permission.BOARD_DELETE_OWN,
    Permission.BOARD_UPDATE_ALL,
    Permission.BOARD_UPDATE_OWN,
    Permission.BOARD_UPLOAD_AVATAR,
    Permission.BOARD_UPLOAD_BANNER,
    Permission.BOARD_VIEW_ALL,
  ],

  MATCH_MANAGEMENT: [
    Permission.MATCHES_CREATE,
    Permission.MATCHES_DELETE,
    Permission.MATCHES_SYNC,
    Permission.MATCHES_UPDATE_ALL,
    Permission.MATCHES_UPDATE_OWN,
    Permission.MATCHES_VIEW_ALL,
  ],

  SYSTEM_ADMIN: [
    Permission.ADMIN_MANAGE_PERMISSIONS,
    Permission.ADMIN_MANAGE_ROLES,
    Permission.ADMIN_VIEW_LOGS,
  ],
} as const;

// ============================================================================
// HELPER TYPES
// ============================================================================

export type PermissionString = `${Permission}`;
export type RoleString = `${Role}`;
export type DraftTypeString = `${DraftType}`;
export type DraftStatusString = `${DraftStatus}`;
export type EntityTypeString = `${EntityType}`;

// ============================================================================
// METADATA (for debugging/reference)
// ============================================================================

export const PERMISSIONS_METADATA = {
  total: 77,
  generatedAt: '2026-10-01T01:57:24.502Z',
  permissions: [
  {
    "id": 129,
    "name": "ACCESS_DASHBOARD_CONTACT",
    "description": "Erlaubt Zugriff auf den Dashboard Kontakt-/Anfragen-Tab"
  },
  {
    "id": 3,
    "name": "ACCESS_DASHBOARD_CONTENT",
    "description": "Erlaubt Zugriff auf den Dashboard Content Management Tab"
  },
  {
    "id": 135,
    "name": "ACCESS_DASHBOARD_MATCHES",
    "description": "Zugriff auf die Spielverwaltung im Dashboard"
  },
  {
    "id": 4,
    "name": "ACCESS_DASHBOARD_SPONSORS",
    "description": "Erlaubt Zugriff auf den Dashboard Sponsor Management Tab"
  },
  {
    "id": 5,
    "name": "ACCESS_DASHBOARD_TEAMS",
    "description": "Erlaubt Zugriff auf den Dashboard Team Management Tab"
  },
  {
    "id": 2,
    "name": "ACCESS_DASHBOARD_USERS",
    "description": "Erlaubt Zugriff auf den Dashboard User Management Tab"
  },
  {
    "id": 100,
    "name": "ADMIN_MANAGE_PERMISSIONS",
    "description": "Berechtigungsdefinitionen erstellen und bearbeiten"
  },
  {
    "id": 101,
    "name": "ADMIN_MANAGE_ROLES",
    "description": "Rollendefinitionen erstellen und bearbeiten"
  },
  {
    "id": 99,
    "name": "ADMIN_VIEW_LOGS",
    "description": "System-Logs und Audit-Trails anzeigen"
  },
  {
    "id": 134,
    "name": "APPOINTMENT_MANAGE",
    "description": "Schnuppertermine verwalten (erstellen, bestaetigen, verschieben, absagen)"
  },
  {
    "id": 93,
    "name": "BOARD_CREATE",
    "description": "Vorstandsmitglieder-Profile erstellen"
  },
  {
    "id": 116,
    "name": "BOARD_CREATE_OWN",
    "description": "Eigenes Vorstandsmitglieder-Profil erstellen"
  },
  {
    "id": 97,
    "name": "BOARD_DELETE",
    "description": "Vorstandsmitglieder-Profile löschen"
  },
  {
    "id": 117,
    "name": "BOARD_DELETE_OWN",
    "description": "Eigenes Vorstandsmitglieder-Profil löschen"
  },
  {
    "id": 95,
    "name": "BOARD_UPDATE_ALL",
    "description": "Vorstandsmitglieder-Profile direkt aktualisieren"
  },
  {
    "id": 118,
    "name": "BOARD_UPDATE_OWN",
    "description": "Eigenes Vorstandsmitglieder-Profil aktualisieren"
  },
  {
    "id": 124,
    "name": "BOARD_UPLOAD_AVATAR",
    "description": "Eigenen Vorstands-Avatar hochladen"
  },
  {
    "id": 125,
    "name": "BOARD_UPLOAD_BANNER",
    "description": "Eigenes Vorstands-Banner hochladen"
  },
  {
    "id": 92,
    "name": "BOARD_VIEW_ALL",
    "description": "Alle Vorstandsmitglieder-Profile anzeigen"
  },
  {
    "id": 131,
    "name": "CONTACT_ASSIGN",
    "description": "Kontaktanfragen einem Bearbeiter zuweisen"
  },
  {
    "id": 133,
    "name": "CONTACT_DELETE",
    "description": "Kontaktanfragen loeschen"
  },
  {
    "id": 132,
    "name": "CONTACT_RESPOND",
    "description": "Auf Kontaktanfragen antworten (Nachrichten senden)"
  },
  {
    "id": 130,
    "name": "CONTACT_VIEW_ALL",
    "description": "Alle Kontaktanfragen anzeigen"
  },
  {
    "id": 60,
    "name": "CONTENT_CREATE_PUBLISHED",
    "description": "Beiträge direkt erstellen und veröffentlichen ohne Review"
  },
  {
    "id": 66,
    "name": "CONTENT_DELETE_ALL",
    "description": "Beliebige Beiträge löschen"
  },
  {
    "id": 65,
    "name": "CONTENT_DELETE_OWN",
    "description": "Eigene Beiträge löschen"
  },
  {
    "id": 119,
    "name": "CONTENT_MARK_BREAKING",
    "description": "Nachrichten als Eilmeldung markieren"
  },
  {
    "id": 67,
    "name": "CONTENT_PUBLISH",
    "description": "Entwürfe veröffentlichen"
  },
  {
    "id": 68,
    "name": "CONTENT_UNPUBLISH",
    "description": "Veröffentlichte Beiträge zurückziehen"
  },
  {
    "id": 63,
    "name": "CONTENT_UPDATE_ALL",
    "description": "Beliebige Beiträge direkt aktualisieren (umgeht Review)"
  },
  {
    "id": 62,
    "name": "CONTENT_UPDATE_OWN",
    "description": "Eigene Beiträge direkt aktualisieren"
  },
  {
    "id": 56,
    "name": "CONTENT_VIEW_ALL",
    "description": "Alle Beiträge inkl. Entwürfe anzeigen"
  },
  {
    "id": 58,
    "name": "CONTENT_VIEW_OWN",
    "description": "Eigene Beiträge anzeigen"
  },
  {
    "id": 57,
    "name": "CONTENT_VIEW_PUBLISHED",
    "description": "Nur veröffentlichte Beiträge anzeigen (Standard für alle)"
  },
  {
    "id": 137,
    "name": "MATCHES_CREATE",
    "description": "Neue Spiele anlegen"
  },
  {
    "id": 140,
    "name": "MATCHES_DELETE",
    "description": "Spiele loeschen"
  },
  {
    "id": 141,
    "name": "MATCHES_SYNC",
    "description": "Abgleich mit dem OEFB manuell starten"
  },
  {
    "id": 138,
    "name": "MATCHES_UPDATE_ALL",
    "description": "Alle Spiele bearbeiten (Ergebnis, Spielverlauf, Bericht)"
  },
  {
    "id": 139,
    "name": "MATCHES_UPDATE_OWN",
    "description": "Spiele der eigenen Mannschaften bearbeiten"
  },
  {
    "id": 136,
    "name": "MATCHES_VIEW_ALL",
    "description": "Alle Spiele sehen, auch unveroeffentlichte"
  },
  {
    "id": 126,
    "name": "SETTINGS_BOARD_SORT",
    "description": "Sortierung für Vorstandsmitglieder konfigurieren"
  },
  {
    "id": 128,
    "name": "SETTINGS_TEAM_SORT",
    "description": "Sortierung für Teams konfigurieren"
  },
  {
    "id": 127,
    "name": "SETTINGS_TRAINER_SORT",
    "description": "Sortierung für Trainer konfigurieren"
  },
  {
    "id": 79,
    "name": "SPONSORS_CREATE",
    "description": "Neue Sponsoren erstellen"
  },
  {
    "id": 83,
    "name": "SPONSORS_DELETE",
    "description": "Sponsoren löschen"
  },
  {
    "id": 84,
    "name": "SPONSORS_OVERRIDE_TIER",
    "description": "Automatische Sponsor-Tier-Berechnung überschreiben"
  },
  {
    "id": 81,
    "name": "SPONSORS_UPDATE_ALL",
    "description": "Sponsoren direkt aktualisieren (umgeht Review)"
  },
  {
    "id": 78,
    "name": "SPONSORS_VIEW_ALL",
    "description": "Alle Sponsoren anzeigen"
  },
  {
    "id": 77,
    "name": "TEAMS_ASSIGN_TRAINERS",
    "description": "Trainer zu Teams zuweisen oder entfernen"
  },
  {
    "id": 71,
    "name": "TEAMS_CREATE",
    "description": "Neue Teams erstellen"
  },
  {
    "id": 76,
    "name": "TEAMS_DELETE",
    "description": "Teams löschen"
  },
  {
    "id": 74,
    "name": "TEAMS_UPDATE_ALL",
    "description": "Beliebige Teams direkt aktualisieren (umgeht Review)"
  },
  {
    "id": 111,
    "name": "TEAMS_UPDATE_IMAGE",
    "description": "Team-Bild aktualisieren (gilt für Entwürfe und Veröffentlichte)"
  },
  {
    "id": 109,
    "name": "TEAMS_UPDATE_NAME",
    "description": "Team-Name aktualisieren (gilt für Entwürfe und Veröffentlichte)"
  },
  {
    "id": 73,
    "name": "TEAMS_UPDATE_OWN",
    "description": "Eigene Teams direkt aktualisieren"
  },
  {
    "id": 110,
    "name": "TEAMS_UPDATE_TRAINERS",
    "description": "Zugewiesene Trainer aktualisieren (gilt für Entwürfe und Veröffentlichte)"
  },
  {
    "id": 69,
    "name": "TEAMS_VIEW_ALL",
    "description": "Alle Teams anzeigen"
  },
  {
    "id": 70,
    "name": "TEAMS_VIEW_OWN",
    "description": "Teams anzeigen, denen der Benutzer als Trainer zugewiesen ist"
  },
  {
    "id": 86,
    "name": "TRAINERS_CREATE",
    "description": "Trainer-Profile erstellen"
  },
  {
    "id": 114,
    "name": "TRAINERS_CREATE_OWN",
    "description": "Eigenes Trainer-Profil erstellen"
  },
  {
    "id": 91,
    "name": "TRAINERS_DELETE",
    "description": "Trainer-Profile löschen"
  },
  {
    "id": 115,
    "name": "TRAINERS_DELETE_OWN",
    "description": "Eigenes Trainer-Profil löschen"
  },
  {
    "id": 89,
    "name": "TRAINERS_UPDATE_ALL",
    "description": "Beliebige Trainer-Profile direkt aktualisieren"
  },
  {
    "id": 88,
    "name": "TRAINERS_UPDATE_OWN",
    "description": "Eigenes Trainer-Profil direkt aktualisieren"
  },
  {
    "id": 120,
    "name": "TRAINERS_UPLOAD_AVATAR",
    "description": "Eigenen Trainer-Avatar hochladen"
  },
  {
    "id": 121,
    "name": "TRAINERS_UPLOAD_BANNER",
    "description": "Eigenes Trainer-Banner hochladen"
  },
  {
    "id": 85,
    "name": "TRAINERS_VIEW_ALL",
    "description": "Alle Trainer-Profile anzeigen"
  },
  {
    "id": 15,
    "name": "USERS_CREATE_USER",
    "description": "Neue Benutzer erstellen"
  },
  {
    "id": 54,
    "name": "USERS_DELETE",
    "description": "Benutzerkonten löschen"
  },
  {
    "id": 53,
    "name": "USERS_MANAGE_PERMISSIONS",
    "description": "Berechtigungen und Rollen zu Benutzern zuweisen"
  },
  {
    "id": 55,
    "name": "USERS_RESET_PASSWORD",
    "description": "Passwort-Resets für beliebige Benutzer auslösen"
  },
  {
    "id": 51,
    "name": "USERS_UPDATE_ALL",
    "description": "Beliebige Benutzerprofile direkt aktualisieren (umgeht Review)"
  },
  {
    "id": 50,
    "name": "USERS_UPDATE_OWN",
    "description": "Eigenes Profil direkt aktualisieren ohne Review"
  },
  {
    "id": 122,
    "name": "USERS_UPLOAD_AVATAR",
    "description": "Eigenen Benutzer-Avatar hochladen"
  },
  {
    "id": 123,
    "name": "USERS_UPLOAD_BANNER",
    "description": "Eigenes Benutzer-Banner hochladen"
  },
  {
    "id": 47,
    "name": "USERS_VIEW_ALL",
    "description": "Alle Benutzerprofile anzeigen"
  },
  {
    "id": 48,
    "name": "USERS_VIEW_OWN",
    "description": "Eigenes Profil anzeigen (Standard für alle authentifizierten Benutzer)"
  }
],
} as const;

export const ROLES_METADATA = {
  total: 10,
  generatedAt: '2026-10-01T01:57:24.505Z',
  roles: [
  {
    "id": 2,
    "name": "ADMIN",
    "description": "Super Administrator with full access to all features"
  },
  {
    "id": 12,
    "name": "ANFRAGEN_MANAGER",
    "description": "Anfragen-Manager: verwaltet Kontaktanfragen, Schnupper- und Probetermine und sieht alle Mannschaften"
  },
  {
    "id": 3,
    "name": "CONTENT_EDITOR",
    "description": "Can create and edit content but requires review for publishing"
  },
  {
    "id": 1,
    "name": "CONTENT_MANAGER",
    "description": "Can create and manage news items on the website."
  },
  {
    "id": 13,
    "name": "SPIELE_MANAGER",
    "description": "Verwaltet alle Spiele: Ergebnisse, Spielverlauf, Berichte und OEFB-Abgleich"
  },
  {
    "id": 6,
    "name": "SPONSOR_MANAGER",
    "description": "Full control over sponsor management"
  },
  {
    "id": 4,
    "name": "TEAM_MANAGER",
    "description": "Full control over team management"
  },
  {
    "id": 5,
    "name": "TRAINER",
    "description": "Can manage own team and profile"
  },
  {
    "id": 11,
    "name": "USER",
    "description": "Basic authenticated user with access to own profile only (excludes Trainer/Board profiles)"
  },
  {
    "id": 7,
    "name": "USER_MANAGER",
    "description": "Full control over user management"
  }
],
} as const;
